import axios from "axios";
import config from "../../../config";
import {
  getHaklaiDealDocumentPrefix,
  HAKLAI_DOCUMENT_LABELS
} from "./haklaiDocumentsTexts";

export const HAKLAI_INVALID_ROOT_ERROR_CODE = "HAKLAI_INVALID_ROOT";

const getHaklai2990JsonDownloadName = (dealTaxType, displayIndex, partsAmount) => {
  let fileName = HAKLAI_DOCUMENT_LABELS.form2990Online;
  if (partsAmount > 1) {
    fileName = `${getHaklaiDealDocumentPrefix(displayIndex)} - ${fileName}`;
  }
  if (dealTaxType === "linear") {
    fileName = `${HAKLAI_DOCUMENT_LABELS.oldLinear} - ${fileName}`;
  } else if (dealTaxType === "mutav") {
    fileName = `${HAKLAI_DOCUMENT_LABELS.mutav} - ${fileName}`;
  }
  return `${fileName}.json`;
};

const downloadJson = ({ data, fileName }) => {
  const dataStr = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(data))}`;
  const aElement = document.createElement("a");
  aElement.setAttribute("href", dataStr);
  aElement.setAttribute("download", fileName);
  document.body.appendChild(aElement);
  aElement.click();
  aElement.remove();
};

const getResponseDataOrError = (response) => (
  response?.data || { err: "Problem with the request" }
);

const downloadBlob = ({ data, fileName }) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  if (window.URL.revokeObjectURL) window.URL.revokeObjectURL(url);
};

const readBlobText = (blob) => {
  if (typeof blob.text === "function") return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
};

const getHaklaiBlobFailure = async (data) => {
  if (data?.success === false) return data;
  if (!(data instanceof Blob)) return null;
  const isJsonBlob = String(data.type || "").toLowerCase().includes("json");
  if (!isJsonBlob && data.size >= 250) return null;
  try {
    const parsed = JSON.parse(await readBlobText(data));
    return parsed?.success === false ? parsed : null;
  } catch {
    return null;
  }
};

const generateHaklaiBlobDocument = async ({ url, data, fileName }) => {
  const response = await axios({
    url,
    method: "post",
    responseType: "blob",
    data
  });

  if (!response || !response.data) {
    throw new Error("Problem with request");
  }
  const failure = await getHaklaiBlobFailure(response.data);
  if (failure || response.data.size < 250 || response.data?.success === false) {
    const error = new Error(failure?.message || "Empty document response");
    error.errorCode = failure?.errorCode;
    throw error;
  }

  downloadBlob({ data: response.data, fileName });
  return { success: true };
};

export const getHaklai2990Fields = async (dealId) => {
  const query = new URLSearchParams({
    dealId,
    isJust2990Fields: "true"
  });
  const response = await axios.get(`${config.getDocsFieldsEndpoint}?${query.toString()}`, { preventLoading: true });
  return getResponseDataOrError(response);
};

export const updateHaklai2990Fields = async (dealId, docFields) => {
  const response = await axios.post(config.update2990SpecificFieldsEndpoint, {
    dealId,
    data2990: docFields
  });
  return getResponseDataOrError(response);
};

export const generateHaklai2990Pdf = (docFields) => generateHaklaiBlobDocument({
  url: config.generate2990Endpoint,
  data: docFields,
  fileName: "2990.pdf"
});

export const generateHaklaiDeductions = (dealId) => generateHaklaiBlobDocument({
  url: config.generateDeductionEndpoint,
  data: { dealId },
  fileName: "ניכויים.pdf"
});

export const generateHaklai2990Json = async ({
  docFields,
  dealId,
  dealTaxType,
  haklaiPropertyKey,
  displayIndex,
  partsAmount
}) => {
  if (haklaiPropertyKey === undefined) {
    throw new Error("Haklai Form 2990 property key is required");
  }
  if (
    typeof haklaiPropertyKey !== "string"
    || !haklaiPropertyKey.trim()
    || haklaiPropertyKey.trim() !== haklaiPropertyKey
  ) {
    throw new Error("Haklai Form 2990 property key is invalid");
  }

  const response = await axios({
    url: config.generate2990JsonEndpoint,
    method: "post",
    data: {
      ...docFields,
      dealId,
      dealTaxType,
      haklaiPropertyKey
    }
  });

  if (!response || !response.data) {
    throw new Error("Problem with request");
  }

  if (response.data.success === false) {
    const validationError = new Error(response.data.message || "Problem with request");
    validationError.success = false;
    validationError.fieldErrors = response.data.fieldErrors;
    validationError.firstFieldName = response.data.firstFieldName;
    validationError.errorCode = response.data.errorCode;
    throw validationError;
  }

  downloadJson({
    data: response.data,
    fileName: getHaklai2990JsonDownloadName(
      dealTaxType,
      displayIndex,
      partsAmount
    )
  });
};
